import { Tooltip } from 'antd';
import { AttachmentViewer } from 'components/Vat/Report/tables/transactions/components/AttachmentViewer/index';
import dayjs from 'dayjs';
import { formatDisplayDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getFilesUploadHistoryRequest } from 'store/organisation/actions';
import type { ExcelFileMetadata } from 'store/organisation/models';
import { UploadStatus } from 'store/organisation/models';
import { getFilesUploadHistory } from 'store/organisation/selectors';
import {
  enableDisableRuleRequest,
  getRulesForApplicationConnectionRequest,
} from 'store/receipt-rules/actions';
import { getReceiptRulesForConnection } from 'store/receipt-rules/selectors';
import {
  clearMetadataAttachments,
  fetchMetadataAttachmentsRequest,
} from 'store/vat/actions';
import {
  getLastFetchedMetadataAttachments,
  getMetadataAttachmentLoading,
} from 'store/vat/selectors';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import {
  getUploadStatusShortLabel,
  getUploadStatusTagVariant,
  isDeletedReceiptStatus,
  isUploadStatusError,
} from 'utils/uploadStatusHelpers';
import { PwaIcon } from './Icon';
import {
  PwaChip,
  PwaFilterChips,
  PwaRow,
  PwaSearch,
  PwaSection,
  PwaSheet,
  PwaSwitch,
  PwaThumb,
} from './ui';

export type PwaCaptureMultiCurrencyMode =
  | 'automatic_conversion'
  | 'stop_upload';

export type PwaCapturePageProps = {
  connectionId: string;
  entityName: string;
  canUploadNow: boolean;
  scanAreaEnabled: boolean;
  scanningPreferencesDisabled: boolean;
  onTakePhoto: () => void;
  onUpload: () => void;
  lastFile?: ExcelFileMetadata | null;
  groupByTaxCode: boolean;
  onGroupByTaxCodeChange: (checked: boolean) => void;
  mapItems: boolean;
  onMapItemsChange: (checked: boolean) => void;
  supportsMapItems: boolean;
  multiCurrencyMode: PwaCaptureMultiCurrencyMode;
  onMultiCurrencyModeChange: (mode: PwaCaptureMultiCurrencyMode) => void;
  supportsStopUploadMultiCurrency: boolean;
};

type SheetId = 'history' | 'rules' | 'prefs' | null;
type HistoryFilter = 'all' | 'done' | 'invalid' | 'deleted' | 'year';

const HISTORY_PAGE_SIZE = 20;

const prettySource = (
  src: string | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  if (!src) return '-';
  if (src === 'ONE_DRIVE') {
    return t('organisation.receipts-source-onedrive', { defaultValue: 'OneDrive' });
  }
  if (src === 'GOOGLE_DRIVE') {
    return t('organisation.receipts-connect-folder-google-drive', {
      defaultValue: 'Google Drive',
    });
  }
  if (src === 'MANUAL') {
    return t('organisation.receipts-source-manual', {
      defaultValue: 'Manual Upload',
    });
  }
  if (src === 'EMAIL') {
    return t('organisation.receipts-capture-email-label');
  }
  return String(src);
};

const statusChip = (
  status: unknown
): { tone?: 'ok' | 'warn' | 'danger' | 'brand'; icon?: string; label: string } => {
  if (isDeletedReceiptStatus(status)) {
    return { icon: 'trash', label: getUploadStatusShortLabel(status) };
  }
  const variant = getUploadStatusTagVariant(status);
  const label = getUploadStatusShortLabel(status);
  if (variant === 'success') return { tone: 'ok', icon: 'check', label };
  if (variant === 'error') return { tone: 'danger', icon: 'close', label };
  if (variant === 'info' || variant === 'warning') {
    return { tone: 'warn', icon: 'history', label };
  }
  return { label };
};

const toDisplayDate = (uploadDate: number | string | null | undefined) => {
  if (uploadDate == null || uploadDate === '') return undefined;
  return typeof uploadDate === 'number' ? new Date(uploadDate) : uploadDate;
};

const dayLabel = (
  uploadDate: number | string,
  language: string,
  t: (key: string, options?: Record<string, unknown>) => string
) => {
  const d = dayjs(uploadDate);
  if (!d.isValid()) return '-';
  const today = dayjs().startOf('day');
  const day = d.startOf('day');
  if (day.isSame(today)) return t('common.calendar.today');
  if (day.isSame(today.subtract(1, 'day'))) {
    return t('common.calendar.yesterday', { defaultValue: 'Yesterday' });
  }
  return formatDisplayDate(toDisplayDate(uploadDate), 'D MMM YYYY', language);
};

export const PwaCapturePage: React.FC<PwaCapturePageProps> = ({
  connectionId,
  entityName,
  canUploadNow,
  scanAreaEnabled,
  scanningPreferencesDisabled,
  onTakePhoto,
  onUpload,
  lastFile,
  groupByTaxCode,
  onGroupByTaxCodeChange,
  mapItems,
  onMapItemsChange,
  supportsMapItems,
  multiCurrencyMode,
  onMultiCurrencyModeChange,
  supportsStopUploadMultiCurrency,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [sheet, setSheet] = React.useState<SheetId>(null);
  const [historyFilter, setHistoryFilter] =
    React.useState<HistoryFilter>('all');
  const [historyQuery, setHistoryQuery] = React.useState('');
  const [historyVisible, setHistoryVisible] = React.useState(HISTORY_PAGE_SIZE);
  const [rulesQuery, setRulesQuery] = React.useState('');
  const [currencyOpen, setCurrencyOpen] = React.useState(false);

  const dragZoneRef = React.useRef<HTMLDivElement>(null);
  const closeSheet = React.useCallback(() => setSheet(null), []);
  useOverlayBackHandler(!!sheet, closeSheet, 'pwa-capture-sheet');
  const dragClose = usePwaBottomSheetDragClose(!!sheet, closeSheet, dragZoneRef);

  const logs = useSelector(getFilesUploadHistory);
  const receiptRules = useSelector(getReceiptRulesForConnection) ?? [];
  const metadataAttachments = useSelector(getLastFetchedMetadataAttachments);
  const metadataAttachmentLoading = useSelector(getMetadataAttachmentLoading);

  const files: ExcelFileMetadata[] = Array.isArray(logs?.files)
    ? logs.files
    : [];

  React.useEffect(() => {
    if (!sheet) {
      document.body.style.overflow = '';
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sheet]);

  React.useEffect(() => {
    if (sheet !== 'history') return;
    dispatch(getFilesUploadHistoryRequest(connectionId, 'all'));
    setHistoryVisible(HISTORY_PAGE_SIZE);
  }, [sheet, connectionId, dispatch]);

  React.useEffect(() => {
    if (sheet !== 'rules') return;
    dispatch(getRulesForApplicationConnectionRequest(connectionId));
  }, [sheet, connectionId, dispatch]);

  React.useEffect(
    () => () => {
      dispatch(clearMetadataAttachments());
    },
    [dispatch]
  );

  const openSheet = (id: SheetId) => {
    if (id === 'history' && !scanAreaEnabled) return;
    if (id === 'rules' && !scanAreaEnabled) return;
    if (id === 'prefs' && scanningPreferencesDisabled) return;
    setCurrencyOpen(false);
    setSheet(id);
  };

  const activeRules = receiptRules.filter((r) => r.enabled).length;
  const rulesChipLabel = t('organisation.pwa-capture-rules-on-chip', {
    defaultValue: '{{on}} of {{total}} on',
    on: activeRules,
    total: receiptRules.length,
  });

  const filteredHistory = React.useMemo(() => {
    const q = historyQuery.trim().toLowerCase();
    const yearStart = dayjs().startOf('year');
    return files.filter((file) => {
      const status = file.status;
      if (historyFilter === 'done' && status !== UploadStatus.ACTIVE) {
        return false;
      }
      if (historyFilter === 'invalid' && !isUploadStatusError(status)) {
        return false;
      }
      if (historyFilter === 'deleted' && !isDeletedReceiptStatus(status)) {
        return false;
      }
      if (historyFilter === 'year') {
        const d = dayjs(file.uploadDate);
        if (!d.isValid() || d.isBefore(yearStart)) return false;
      }
      if (!q) return true;
      const name = String(file.fileName ?? '').toLowerCase();
      const by = String(file.user?.email ?? '').toLowerCase();
      return name.includes(q) || by.includes(q);
    });
  }, [files, historyFilter, historyQuery]);

  const historyCounts = React.useMemo(() => {
    const yearStart = dayjs().startOf('year');
    let done = 0;
    let invalid = 0;
    let deleted = 0;
    let year = 0;
    for (const file of files) {
      if (file.status === UploadStatus.ACTIVE) done += 1;
      if (isUploadStatusError(file.status)) invalid += 1;
      if (isDeletedReceiptStatus(file.status)) deleted += 1;
      const d = dayjs(file.uploadDate);
      if (d.isValid() && !d.isBefore(yearStart)) year += 1;
    }
    return { all: files.length, done, invalid, deleted, year };
  }, [files]);

  const groupedHistory = React.useMemo(() => {
    const visible = filteredHistory.slice(0, historyVisible);
    const groups: { day: string; items: ExcelFileMetadata[] }[] = [];
    const map = new Map<string, ExcelFileMetadata[]>();
    for (const file of visible) {
      const key = dayLabel(file.uploadDate, i18n.language, t);
      const list = map.get(key) ?? [];
      list.push(file);
      map.set(key, list);
    }
    for (const [day, items] of map) {
      groups.push({ day, items });
    }
    return groups;
  }, [filteredHistory, historyVisible, i18n.language, t]);

  const filteredRules = React.useMemo(() => {
    const q = rulesQuery.trim().toLowerCase();
    if (!q) return receiptRules;
    return receiptRules.filter((rule) =>
      String(rule.name ?? '')
        .toLowerCase()
        .includes(q)
    );
  }, [receiptRules, rulesQuery]);

  const openFilePreview = (log: ExcelFileMetadata) => {
    const fileName = String(log.fileName ?? '').trim() || '-';
    const metadataId = String(log.excelMetadataId ?? '').trim();
    if (!metadataId || !connectionId) return;
    dispatch(fetchMetadataAttachmentsRequest(connectionId, metadataId, fileName));
  };

  const lastUploadChip = lastFile ? statusChip(lastFile.status) : null;
  const multiCurrencyLabel =
    multiCurrencyMode === 'stop_upload'
      ? t('organisation.scanning-preferences.multi-currency-stop-title')
      : t('organisation.scanning-preferences.multi-currency-automatic-title');

  return (
    <div data-shell="pwa" className="pwa pwa-capture-host scope-pwa-capture-page">
      <main className="pwa-main">
        <div style={{ height: 16 }} />
        <PwaSection>
          <div className="pwa-capture-hero">
            <div>
              <h3 className="pwa-capture-title">
                <PwaIcon
                  name="sparkles"
                  size={20}
                  className="pwa-sparkle"
                  stroke={2}
                />
                {t('organisation.pwa-capture-title', {
                  defaultValue: 'Capture a receipt or bill',
                })}
              </h3>
              <p>
                {t('organisation.pwa-capture-subtitle', {
                  defaultValue:
                    'We read the totals, VAT and supplier, then queue it for review.',
                })}
              </p>
            </div>
            <div className="pwa-capture-actions">
              <button
                type="button"
                className="pwa-btn primary"
                disabled={!canUploadNow}
                onClick={onTakePhoto}
              >
                <PwaIcon name="camera" size={22} />
                {t('organisation.receipts-take-photo', {
                  defaultValue: 'Take photo',
                })}
              </button>
              <button
                type="button"
                className="pwa-btn secondary"
                disabled={!canUploadNow}
                onClick={onUpload}
              >
                <PwaIcon name="upload" size={20} />
                {t('organisation.pwa-capture-upload', {
                  defaultValue: 'Upload',
                })}
              </button>
            </div>
          </div>
        </PwaSection>

        <PwaSection
          title={t('organisation.last-uploaded-file', {
            defaultValue: 'Last upload',
          })}
          action={
            <button type="button" onClick={() => openSheet('history')}>
              {t('organisation.pwa-capture-see-all', {
                defaultValue: 'See all',
              })}
            </button>
          }
        >
          {lastFile ? (
            <button
              type="button"
              className="pwa-card"
              onClick={() => openSheet('history')}
            >
              <div className="pwa-docpreview">
                <PwaThumb sm />
                <div className="pwa-row-body">
                  <span className="pwa-row-title pwa-mono">
                    {lastFile.fileName || '-'}
                  </span>
                  <span className="pwa-row-meta">
                    {t('organisation.uploaded-on', {
                      defaultValue: 'Uploaded',
                    })}{' '}
                    {formatDisplayDate(
                      toDisplayDate(lastFile.uploadDate),
                      'DD/MM/YYYY',
                      i18n.language
                    )}
                    {lastFile.user?.email
                      ? ` · ${lastFile.user.email}`
                      : ''}
                  </span>
                </div>
                {lastUploadChip ? (
                  <PwaChip tone={lastUploadChip.tone} icon={lastUploadChip.icon}>
                    {lastUploadChip.label}
                  </PwaChip>
                ) : null}
              </div>
            </button>
          ) : (
            <div className="pwa-card" style={{ cursor: 'default' }}>
              <div className="pwa-docpreview">
                <PwaThumb sm />
                <div className="pwa-row-body">
                  <span className="pwa-row-title">
                    {t('organisation.pwa-capture-no-uploads', {
                      defaultValue: 'No documents yet',
                    })}
                  </span>
                  <span className="pwa-row-meta">
                    {t('organisation.pwa-capture-no-uploads-sub', {
                      defaultValue: 'Captured files will show up here',
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </PwaSection>

        <PwaSection
          title={t('organisation.pwa-capture-settings', {
            defaultValue: 'Capture settings',
          })}
        >
          <div className="pwa-list">
            <PwaRow
              icon="history"
              title={t('organisation.data-capture-history', {
                defaultValue: 'Capture history',
              })}
              sub={t('organisation.processed-documents-log-subtitle', {
                defaultValue: 'Every document processed for this entity',
              })}
              chevron
              disabled={!scanAreaEnabled}
              onClick={() => openSheet('history')}
            />
            <PwaRow
              icon="sparkles"
              title={t('organisation.extraction-rules.drawer-title', {
                defaultValue: 'Capture rules',
              })}
              sub={t('organisation.extraction-rules.drawer-intro', {
                defaultValue: 'Auto-fill fields when documents are scanned',
              })}
              end={
                receiptRules.length > 0 ? (
                  <PwaChip tone="brand">{rulesChipLabel}</PwaChip>
                ) : undefined
              }
              chevron
              disabled={!scanAreaEnabled}
              onClick={() => openSheet('rules')}
            />
            <PwaRow
              icon="sliders"
              title={t('organisation.scanning-preferences.title', {
                defaultValue: 'Scanning preferences',
              })}
              sub={t('organisation.pwa-capture-prefs-sub', {
                defaultValue: 'Line grouping, currency, inventory',
              })}
              chevron
              disabled={scanningPreferencesDisabled}
              onClick={() => openSheet('prefs')}
            />
          </div>
        </PwaSection>
      </main>

      {sheet === 'history' ? (
        <PwaSheet
          full
          title={t('organisation.data-capture-history', {
            defaultValue: 'Capture history',
          })}
          sub={t('organisation.processed-documents-log-subtitle', {
            defaultValue: 'All documents processed for this entity',
          })}
          onClose={closeSheet}
          dragZoneRef={dragZoneRef}
          dragCloseHandlers={dragClose}
        >
          <PwaSearch
            id="pwa-capture-history-search"
            placeholder={t('organisation.pwa-capture-history-search', {
              defaultValue: 'Search by file or supplier',
            })}
            value={historyQuery}
            onChange={setHistoryQuery}
          />
          <div style={{ margin: '0 -16px' }}>
            <PwaFilterChips
              value={historyFilter}
              onChange={(v) => setHistoryFilter(v as HistoryFilter)}
              items={[
                {
                  value: 'all',
                  label: t('common.all', { defaultValue: 'All' }),
                  count: historyCounts.all,
                },
                {
                  value: 'done',
                  label: t('common.done', { defaultValue: 'Processed' }),
                  count: historyCounts.done,
                },
                {
                  value: 'invalid',
                  label: t('upload-status.invalid-short', {
                    defaultValue: 'Unreadable',
                  }),
                  count: historyCounts.invalid,
                },
                {
                  value: 'deleted',
                  label: t('upload-status.deleted', { defaultValue: 'Deleted' }),
                  count: historyCounts.deleted,
                },
                {
                  value: 'year',
                  label: t('organisation.pwa-capture-this-year', {
                    defaultValue: 'This year',
                  }),
                  icon: 'calendar',
                  count: historyCounts.year,
                },
              ]}
            />
          </div>
          {groupedHistory.length === 0 ? (
            <div className="pwa-row static">
              <span className="pwa-row-body">
                <span className="pwa-row-sub">
                  {t('organisation.pwa-capture-history-empty', {
                    defaultValue: 'No documents match these filters.',
                  })}
                </span>
              </span>
            </div>
          ) : (
            groupedHistory.map((group) => (
              <React.Fragment key={group.day}>
                <div className="pwa-daygroup">{group.day}</div>
                <div className="pwa-list">
                  {group.items.map((item) => {
                    const chip = statusChip(item.status);
                    return (
                      <PwaRow
                        key={item.excelMetadataId || item.fileName}
                        lead={<PwaThumb sm />}
                        title={
                          <span className="pwa-mono">
                            {item.fileName || '-'}
                          </span>
                        }
                        sub={`${prettySource(item.uploadSource, t)} · ${formatDisplayDate(
                          toDisplayDate(item.uploadDate),
                          'HH:mm',
                          i18n.language
                        )}`}
                        end={
                          <PwaChip tone={chip.tone} icon={chip.icon}>
                            {chip.label}
                          </PwaChip>
                        }
                        onClick={() => openFilePreview(item)}
                      />
                    );
                  })}
                </div>
              </React.Fragment>
            ))
          )}
          {filteredHistory.length > historyVisible ? (
            <button
              type="button"
              className="pwa-btn secondary sm pwa-loadmore"
              onClick={() =>
                setHistoryVisible((n) => n + HISTORY_PAGE_SIZE)
              }
            >
              {t('main.load-more', { defaultValue: 'Load more' })}
            </button>
          ) : null}
        </PwaSheet>
      ) : null}

      {sheet === 'rules' ? (
        <PwaSheet
          full
          title={t('organisation.extraction-rules.drawer-title', {
            defaultValue: 'Capture rules',
          })}
          sub={t('organisation.pwa-capture-rules-sub', {
            defaultValue:
              '{{count}} of {{total}} rules on · applied when a document is scanned',
            count: activeRules,
            total: receiptRules.length,
          })}
          onClose={closeSheet}
          dragZoneRef={dragZoneRef}
          dragCloseHandlers={dragClose}
        >
          <PwaSearch
            id="pwa-capture-rules-search"
            placeholder={t('organisation.pwa-capture-rules-search', {
              defaultValue: 'Search rules',
            })}
            value={rulesQuery}
            onChange={setRulesQuery}
          />
          <div className="pwa-list">
            {filteredRules.length === 0 ? (
              <div className="pwa-row static">
                <span className="pwa-row-body">
                  <span className="pwa-row-sub">
                    {t('organisation.extraction-rules.empty', {
                      defaultValue: 'No capture rules yet.',
                    })}
                  </span>
                </span>
              </div>
            ) : (
              filteredRules.map((rule) => (
                <div key={rule.id ?? rule.name} className="pwa-row static">
                  <span className="pwa-row-body">
                    <span
                      className="pwa-row-title wrap"
                      style={{
                        fontWeight: 500,
                        color: rule.enabled ? undefined : 'var(--gt-ink-2)',
                      }}
                    >
                      {rule.name}
                    </span>
                    {rule.createdDate ? (
                      <span className="pwa-row-meta">
                        {t('organisation.pwa-capture-rule-created', {
                          defaultValue: 'Created',
                        })}{' '}
                        {formatDisplayDate(
                          toDisplayDate(rule.createdDate),
                          'DD/MM/YYYY',
                          i18n.language
                        )}
                      </span>
                    ) : null}
                  </span>
                  <span className="pwa-row-end">
                    <PwaSwitch
                      checked={!!rule.enabled}
                      label={rule.name}
                      disabled={!rule.id}
                      onChange={(enabled) => {
                        if (!rule.id) return;
                        dispatch(
                          enableDisableRuleRequest(
                            connectionId,
                            rule.id,
                            enabled
                          )
                        );
                      }}
                    />
                  </span>
                </div>
              ))
            )}
          </div>
        </PwaSheet>
      ) : null}

      {sheet === 'prefs' ? (
        <PwaSheet
          title={t('organisation.scanning-preferences.title', {
            defaultValue: 'Scanning preferences',
          })}
          onClose={closeSheet}
          dragZoneRef={dragZoneRef}
          dragCloseHandlers={dragClose}
        >
          <div className="pwa-list">
            <div className="pwa-row static">
              <span className="pwa-row-body">
                <span className="pwa-row-title pwa-row-title--with-tip">
                  {t('organisation.group-lines-by-tax-code-label')}
                  <Tooltip
                    title={t(
                      'organisation.scanning-preferences.group-by-tax-code-description'
                    )}
                    trigger={['hover', 'click']}
                    zIndex={1600}
                  >
                    <button
                      type="button"
                      className="pwa-info-tip"
                      aria-label={t('common.more-information', {
                        defaultValue: 'More information',
                      })}
                    >
                      <PwaIcon name="info" size={16} />
                    </button>
                  </Tooltip>
                </span>
              </span>
              <PwaSwitch
                checked={groupByTaxCode}
                label={t('organisation.group-lines-by-tax-code-label')}
                disabled={scanningPreferencesDisabled}
                onChange={onGroupByTaxCodeChange}
              />
            </div>
            {supportsMapItems ? (
              <div className="pwa-row static">
                <span className="pwa-row-body">
                  <span className="pwa-row-title pwa-row-title--with-tip">
                    {t('organisation.scanning-preferences.map-items-label')}
                    <Tooltip
                      title={t(
                        'organisation.scanning-preferences.map-items-description'
                      )}
                      trigger={['hover', 'click']}
                      zIndex={1600}
                    >
                      <button
                        type="button"
                        className="pwa-info-tip"
                        aria-label={t('common.more-information', {
                          defaultValue: 'More information',
                        })}
                      >
                        <PwaIcon name="info" size={16} />
                      </button>
                    </Tooltip>
                  </span>
                </span>
                <PwaSwitch
                  checked={mapItems}
                  label={t('organisation.scanning-preferences.map-items-label')}
                  disabled={scanningPreferencesDisabled}
                  onChange={onMapItemsChange}
                />
              </div>
            ) : null}
          </div>
          <div className="pwa-list">
            <PwaRow
              icon="currency"
              title={t('organisation.pwa-capture-multi-currency', {
                defaultValue: 'Multi-currency',
              })}
              sub={multiCurrencyLabel}
              chevron
              disabled={scanningPreferencesDisabled}
              onClick={() => setCurrencyOpen((open) => !open)}
            />
            {currencyOpen ? (
              <div className="pwa-capture-prefs-expand">
                <Tooltip
                  title={t(
                    'organisation.scanning-preferences.multi-currency-automatic-description'
                  )}
                  trigger={['hover', 'click']}
                  zIndex={1600}
                >
                  <button
                    type="button"
                    className="pwa-capture-prefs-option"
                    aria-pressed={multiCurrencyMode === 'automatic_conversion'}
                    onClick={() =>
                      onMultiCurrencyModeChange('automatic_conversion')
                    }
                  >
                    <b>
                      {t(
                        'organisation.scanning-preferences.multi-currency-automatic-title'
                      )}
                    </b>
                  </button>
                </Tooltip>
                <Tooltip
                  title={
                    supportsStopUploadMultiCurrency
                      ? t(
                          'organisation.scanning-preferences.multi-currency-stop-description'
                        )
                      : t(
                          'organisation.scanning-preferences.multi-currency-stop-disabled'
                        )
                  }
                  trigger={['hover', 'click']}
                  zIndex={1600}
                >
                  <button
                    type="button"
                    className="pwa-capture-prefs-option"
                    aria-pressed={multiCurrencyMode === 'stop_upload'}
                    disabled={!supportsStopUploadMultiCurrency}
                    onClick={() => {
                      if (!supportsStopUploadMultiCurrency) return;
                      onMultiCurrencyModeChange('stop_upload');
                    }}
                  >
                    <b>
                      {t(
                        'organisation.scanning-preferences.multi-currency-stop-title'
                      )}
                    </b>
                  </button>
                </Tooltip>
              </div>
            ) : null}
          </div>
        </PwaSheet>
      ) : null}

      {metadataAttachments.metadataId ? (
        <AttachmentViewer
          onClose={() => dispatch(clearMetadataAttachments())}
          transactionReference={metadataAttachments.fileName || ''}
          attachments={metadataAttachments.attachments}
          attachmentLoading={metadataAttachmentLoading}
          providerName=""
          attachmentSourceUrl=""
          showUpload={false}
          standaloneWidth={720}
          standaloneFullHeight
          centered
        />
      ) : null}
    </div>
  );
};

export default PwaCapturePage;
